import { authV2Url } from '@/core/app/authUrls'
import { logger } from '@/core/app/logger'
import { AuthCheckResponseSchema, TokenRefreshSuccessSchema } from '@/schemas/v2/auth'
import { useTokenStore } from '@/store/useTokenStore'

/** 認証系 fetch（auth/check・token/refresh）のタイムアウト（ms） */
export const AUTH_FETCH_TIMEOUT_MS = 10_000

/** 認証系 fetch がタイムアウトしたことを表すエラー（DOMException の name 判定に依存しないため専用型にする） */
export class AuthFetchTimeoutError extends Error {
  constructor(public readonly url: string) {
    super(`auth fetch timeout: ${url}`)
    this.name = 'AuthFetchTimeoutError'
  }
}

/**
 * タイムアウト付き fetch。応答しない場合に認証チェックが終わらずスケルトンが固着するのを防ぐ（#3452）。
 * AbortSignal.timeout() は古い iOS Safari で使えないため AbortController + setTimeout で実装する。
 * タイマーはレスポンスヘッダー受信までを対象にする（本文の読み込みは呼び出し側）。
 */
export async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number = AUTH_FETCH_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } catch (e) {
    if (controller.signal.aborted) throw new AuthFetchTimeoutError(url)
    throw e
  } finally {
    clearTimeout(timer)
  }
}

export type CheckAuthResult = { isAuthenticated: boolean; idToken: string | null; expiresAt: number | null }

/**
 * アプリ起動時（phase1）の認証チェック。
 *
 * - useTokenStore.loading は「認証状態が確定したか」を表す。/token/refresh の結果が出るまで false にしない（#3627）。
 *   /qr などは loading === false && idToken === null を未ログインとみなすため、途中で clearToken すると誤判定する。
 * - どの経路で終わっても終了時には loading を false にする。phase2 の waitForTokenLoad は loading === false を待つため、
 *   true のまま残すとスケルトンが固着する（#3452）。
 *
 * @param onAuthenticated ログイン確定時に呼ぶ（sheets/spots/userProfile のキャッシュ削除）。Provider から注入する
 * @internal テスト容易性のためモジュール関数として切り出し
 */
export async function checkAuthWithStore(campaignSlug: string, onAuthenticated: () => void): Promise<CheckAuthResult> {
  const logBase = { xstate: 'lifecycle' } as const

  // 1. Zustand キャッシュが有効なら早期リターン（setToken 済みなので loading は false）
  const cached = useTokenStore.getState()
  const nowSec = Math.floor(Date.now() / 1000)
  if (cached.idToken && cached.expiresAt && cached.expiresAt > nowSec) {
    logger.info({ ...logBase, msg: 'checkAuth: Zustandキャッシュ有効 → 認証済み' })
    return { isAuthenticated: true, idToken: cached.idToken, expiresAt: cached.expiresAt }
  }

  try {
    // 2. /auth/check で Cookie を検証
    try {
      logger.info({
        ...logBase,
        fetch_method: 'GET',
        fetch_url: authV2Url.check(campaignSlug),
        msg: 'checkAuth: auth/check 開始',
      })
      const res = await fetchWithTimeout(authV2Url.check(campaignSlug), { credentials: 'include' })
      // 2xx 以外は JSON パースをスキップして token/refresh へ
      if (res.ok) {
        const parsed = AuthCheckResponseSchema.parse(await res.json())
        if (parsed.authenticated) {
          logger.info({ ...logBase, msg: 'checkAuth: auth/check 成功 → 認証済み' })
          useTokenStore.getState().setToken(parsed.idToken, parsed.expiresAt)
          onAuthenticated()
          return { isAuthenticated: true, idToken: parsed.idToken, expiresAt: parsed.expiresAt }
        }
      }
    } catch (e) {
      // 通信エラー・タイムアウト・パースエラーは token/refresh へ続行
      logger.error({ ...logBase, msg: `checkAuth: auth/check エラー: ${String(e)}` })
    }

    // 3. /token/refresh でトークン再発行。結果が出るまで clearToken しない（#3627）
    try {
      logger.info({
        ...logBase,
        fetch_method: 'POST',
        fetch_url: authV2Url.tokenRefresh(campaignSlug),
        msg: 'checkAuth: token/refresh 開始',
      })
      const res = await fetchWithTimeout(authV2Url.tokenRefresh(campaignSlug), {
        method: 'POST',
        credentials: 'include',
      })
      // エラー本文をパースすると ZodError になり原因が読みにくいため、先にステータスで判定する
      if (!res.ok) throw new Error(`token/refresh failed: ${res.status}`)
      const parsed = TokenRefreshSuccessSchema.parse(await res.json())
      logger.info({ ...logBase, msg: 'checkAuth: token/refresh 成功 → 認証済み' })
      useTokenStore.getState().setToken(parsed.idToken, parsed.expiresAt)
      onAuthenticated()
      return { isAuthenticated: true, idToken: parsed.idToken, expiresAt: parsed.expiresAt }
    } catch (e) {
      logger.error({ ...logBase, msg: `checkAuth: token/refresh エラー: ${String(e)}` })
    }

    // 4. 未認証で確定。ここで初めて store を空にする（期限切れトークンが残っていれば消す）
    logger.info({ ...logBase, msg: 'checkAuth: 未認証' })
    useTokenStore.getState().clearToken()
    return { isAuthenticated: false, idToken: null, expiresAt: null }
  } finally {
    // 安全網: onAuthenticated の例外など想定外の経路でも loading を true のまま残さない（#3452）
    if (useTokenStore.getState().loading) {
      useTokenStore.getState().clearToken()
    }
  }
}
